import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiFetch } from './client'

// Types

export interface SimulationCampaignStats {
  total: number
  contacted: number
  remaining: number
}

export interface SimulationCampaign {
  id: number
  name: string
  description: string
  total_entries: number
  created_at: number
  updated_at: number
  stats: SimulationCampaignStats
}

export interface SimulationEntry {
  id: number
  campaign_id: number
  simulation_name: string
  personality_name: string
  personality_config: string
  scenario_name: string
  scenario_config: string
  description: string
  status: string
  disposition: string | null
  notes: string
  call_id: string | null
  called_at: number | null
  created_at: number
}

interface SimulationConfig {
  simulations: {
    name: string
    personality_config: string
    scenario_config: string
    description: string
  }[]
  target_assistant_type: string
  target_squad_id: string
  personalities: { file: string; name: string }[]
  scenarios: { file: string; name: string }[]
}

interface TriggerCallResponse {
  success: boolean
  call_id: string
  entry_id: number
  listen_url: string
  control_url: string
  message: string
}

// Hooks

export function useSimulationConfigs() {
  return useQuery({
    queryKey: ['simulation-configs'],
    queryFn: () => apiFetch<SimulationConfig>('/simulations/configs'),
  })
}

export function useSimulationCampaigns() {
  return useQuery({
    queryKey: ['simulation-campaigns'],
    queryFn: () =>
      apiFetch<{ simulation_campaigns: SimulationCampaign[] }>('/simulations/campaigns'),
    select: (data) => data.simulation_campaigns,
  })
}

export function useSimulationCampaign(id: number | undefined) {
  return useQuery({
    queryKey: ['simulation-campaign', id],
    queryFn: () =>
      apiFetch<{ simulation_campaign: SimulationCampaign }>(`/simulations/campaigns/${id}`),
    enabled: id != null,
    select: (data) => data.simulation_campaign,
  })
}

export function useSimulationEntries(
  campaignId: number | undefined,
  params?: { limit?: number; offset?: number; status?: string }
) {
  return useQuery({
    queryKey: ['simulation-entries', campaignId, params],
    queryFn: () => {
      const sp = new URLSearchParams()
      if (params?.limit) sp.set('limit', String(params.limit))
      if (params?.offset) sp.set('offset', String(params.offset))
      if (params?.status) sp.set('status', params.status)
      const qs = sp.toString()
      return apiFetch<{ entries: SimulationEntry[]; total: number }>(
        `/simulations/campaigns/${campaignId}/entries${qs ? `?${qs}` : ''}`
      )
    },
    enabled: campaignId != null,
  })
}

export function useNextSimulationEntries(campaignId: number | undefined, count = 1) {
  return useQuery({
    queryKey: ['simulation-next', campaignId, count],
    queryFn: () =>
      apiFetch<{ entries: SimulationEntry[]; remaining: number }>(
        `/simulations/campaigns/${campaignId}/next?count=${count}`
      ),
    enabled: campaignId != null,
  })
}

export function useCreateSimulationCampaign() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (params: { name?: string; description?: string }) =>
      apiFetch<{ simulation_campaign: SimulationCampaign; entries_created: number }>(
        '/simulations/campaigns',
        {
          method: 'POST',
          body: JSON.stringify(params),
        }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['simulation-campaigns'] })
    },
  })
}

export function useDeleteSimulationCampaign() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) =>
      apiFetch<{ success: boolean }>(`/simulations/campaigns/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['simulation-campaigns'] })
    },
  })
}

export function useResetSimulationCampaign() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) =>
      apiFetch<{ success: boolean; entries_reset: number }>(
        `/simulations/campaigns/${id}/reset`,
        { method: 'POST' },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['simulation-campaigns'] })
      queryClient.invalidateQueries({ queryKey: ['simulation-campaign'] })
      queryClient.invalidateQueries({ queryKey: ['simulation-entries'] })
      queryClient.invalidateQueries({ queryKey: ['simulation-next'] })
    },
  })
}

export function useTriggerSimulationCall() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ campaignId, entryId }: { campaignId: number; entryId: number }) =>
      apiFetch<TriggerCallResponse>(
        `/simulations/campaigns/${campaignId}/entries/${entryId}/call`,
        { method: 'POST' }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['simulation-entries'] })
      queryClient.invalidateQueries({ queryKey: ['simulation-next'] })
      queryClient.invalidateQueries({ queryKey: ['simulation-campaign'] })
      queryClient.invalidateQueries({ queryKey: ['simulation-campaigns'] })
    },
  })
}

export function useUpdateSimulationEntry() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      entryId,
      ...data
    }: {
      entryId: number
      status?: string
      disposition?: string
      notes?: string
      call_id?: string
      called_at?: number
    }) =>
      apiFetch<{ entry: SimulationEntry }>(`/simulations/entries/${entryId}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['simulation-entries'] })
      queryClient.invalidateQueries({ queryKey: ['simulation-next'] })
      queryClient.invalidateQueries({ queryKey: ['simulation-campaign'] })
      queryClient.invalidateQueries({ queryKey: ['simulation-campaigns'] })
    },
  })
}
